import { Button, Loading, MultiSelect } from '@carbon/react';
import { FilterReset } from '@carbon/react/icons';
import { LTD_FACETS, LTD_FACET_LABELS, type LtdFacet } from './ltd-candidates';
import styles from './add-wellbore-tearsheet.module.scss';

interface LtdFilterPanelProps {
  /** The values still reachable per facet, given the OTHER active filters. */
  available: Partial<Record<LtdFacet, string[]>>;
  selected: Partial<Record<LtdFacet, string[]>>;
  onChange: (facet: LtdFacet, values: string[]) => void;
  onReset: () => void;
  disabled?: boolean;
  /** Whether the facet option lists are being refetched for a new filter combination. */
  loading?: boolean;
}

/**
 * The sheet's LTD filter rail.
 *
 * PROGRESSIVE: the options come from the backend's own facet lists, which are computed
 * against every OTHER active filter rather than against the unfiltered catalog. That is what
 * makes a combination matching nothing unbuildable — and what lets a facet be widened again
 * after narrowing, since its own selection is excluded from its option list.
 *
 * Multi-select per facet, because the endpoint permits each as an array and a user narrowing
 * to two formations should not have to choose between them.
 * @param {LtdFilterPanelProps} props The option lists, the selection and the handlers.
 * @return {JSX.Element} The rail.
 */
export function LtdFilterPanel({
  available,
  selected,
  onChange,
  onReset,
  disabled = false,
  loading = false,
}: LtdFilterPanelProps) {
  const activeCount = LTD_FACETS.filter(
    (facet) => (selected[facet] ?? []).length > 0
  ).length;

  return (
    <div className={styles.filterPanel}>
      <div className={styles.filterHeader}>
        <h4 className={styles.paneTitle}>LTD Filters</h4>
        <Button
          kind="ghost"
          size="sm"
          hasIconOnly
          renderIcon={FilterReset}
          iconDescription="Reset filters"
          tooltipPosition="left"
          disabled={disabled || activeCount === 0}
          onClick={onReset}
        />
      </div>

      {LTD_FACETS.map((facet) => {
        const items = available[facet] ?? [];
        const chosen = selected[facet] ?? [];

        return (
          <MultiSelect
            key={facet}
            id={`ltd-facet-${facet}`}
            className={styles.facet}
            titleText={
              <span className={styles.facetLabel}>
                {LTD_FACET_LABELS[facet]}
                {/* Hidden from the a11y tree on purpose: `titleText` renders inside the
                    <label>, so the spinner's own description would otherwise join the
                    combobox's accessible name and make name-based queries brittle. */}
                {loading && (
                  <span aria-hidden="true">
                    <Loading
                      small
                      withOverlay={false}
                      description="Loading options"
                    />
                  </span>
                )}
              </span>
            }
            label={chosen.length > 0 ? `${chosen.length} selected` : 'All'}
            items={items}
            initialSelectedItems={chosen}
            selectedItems={chosen}
            // Nothing reachable means every remaining option is already excluded by the
            // filters above it — offering an empty dropdown would read as a failed load.
            disabled={disabled || items.length === 0}
            onChange={({ selectedItems }) =>
              onChange(facet, selectedItems ?? [])
            }
          />
        );
      })}
    </div>
  );
}
